/**
 * One parent organization: identity form, then the two write-on-change sections
 * (ownership events, history timeline).
 *
 * The form buffers behind its own Save, like the manufacturer detail form. The
 * two sections below it do NOT — each persists the moment a change is made —
 * and they only appear once the organization exists, because every one of their
 * writes and uploads is keyed on the document id.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import {
  countEntityLinks,
  describeLinkLoss,
  withholdLinkLosingDescriptions,
} from '../../services/descriptionLinkGuard';
import { DescriptionLinksWarning } from '../common/EnrichHintDialog';
import type { ParentOrganization } from '../../types';
import {
  createParentOrganization,
  deleteParentOrganization,
  enrichParentOrgWithAI,
  updateParentOrganization,
  uploadParentOrganizationLogo,
  type ParentOrganizationPatch,
} from '../../services/firebase';
import { Button } from '../common/Button';
import { Modal } from '../common/Modal';
import { CountrySelect } from '../common/CountrySelect';
import { RichTextEditor } from '../common/RichTextEditor';
import { TagInput } from '../CuratedWatches/TagInput';
import { EntityPicker, optionMatches } from './EntityPicker';
import { OwnershipEventsSection } from './OwnershipEventsSection';
import { ParentOrgTimelineSection } from './ParentOrgTimelineSection';
import { PortfolioSection } from './PortfolioSection';
import { ancestryPath } from './orgAncestry';
import { forbiddenParentIds, slugifyParentOrgValue } from './parentOrgSeed';

const STATUSES: Array<NonNullable<ParentOrganization['status']>> = ['active', 'defunct', 'divested'];

/**
 * A stored value as it should READ in the AI review table. The description is
 * rich-text HTML in the form, so without this the "Current" column shows raw
 * `<p>…</p>` markup beside the suggestion's plain prose, which makes the two
 * look more different than they are. Display only — nothing written is touched.
 */
export function reviewText(value: string): string {
  return value
    .replace(/<\/(p|div|li|h[1-6])>|<br\s*\/?>/gi, ' ')
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    // Last, or "&amp;lt;" (a literal "&lt;" in the text) decodes twice into "<".
    .replace(/&amp;/g, '&')
    .replace(/\s+/g, ' ')
    .trim();
}

interface Form {
  name: string;
  displayName: string;
  description: string;
  alternativeNames: string[];
  abbreviations: string[];
  logo: string;
  logoSourceUrl: string;
  country: string;
  founded: string;
  website: string;
  status: NonNullable<ParentOrganization['status']>;
  parentOrgId: string;
}

function toForm(org: ParentOrganization | null): Form {
  return {
    name: org?.name ?? '',
    displayName: org?.displayName ?? '',
    description: org?.description ?? '',
    alternativeNames: org?.alternativeNames ?? [],
    abbreviations: org?.abbreviations ?? [],
    logo: org?.logo ?? '',
    logoSourceUrl: org?.logoSourceUrl ?? '',
    country: org?.country ?? '',
    founded: typeof org?.founded === 'number' ? String(org.founded) : '',
    website: org?.website ?? '',
    status: org?.status ?? 'active',
    parentOrgId: org?.parentOrgId ?? '',
  };
}

/**
 * The form as a patch against what is stored. A field the curator emptied
 * becomes `null` — cleared — rather than undefined, which would be dropped and
 * leave the old value in place.
 */
export function formToPatch(form: Form): ParentOrganizationPatch {
  const text = (v: string) => v.trim() || null;
  const list = (v: string[]) => {
    const cleaned = v.map((s) => s.trim()).filter(Boolean);
    return cleaned.length ? cleaned : null;
  };
  const founded = form.founded.trim() === '' ? null : Number(form.founded);
  return {
    name: form.name.trim(),
    displayName: text(form.displayName),
    description: text(form.description),
    alternativeNames: list(form.alternativeNames),
    abbreviations: list(form.abbreviations),
    logo: text(form.logo),
    logoSourceUrl: text(form.logoSourceUrl),
    country: text(form.country),
    founded: founded !== null && Number.isInteger(founded) ? founded : null,
    website: text(form.website),
    status: form.status,
    parentOrgId: text(form.parentOrgId),
  };
}

interface Props {
  isOpen: boolean;
  /** `null` creates a new organization. */
  org: ParentOrganization | null;
  orgs: ParentOrganization[];
  onClose: () => void;
  /** After any write — the host reloads its list. Receives the saved doc. */
  onSaved: (org: ParentOrganization) => void;
  onDeleted: (id: string) => void;
  /** Open a brand's editor on another tab — omitted, the chips are inert. */
  onOpenBrand?: (brandId: string) => void;
  /** Open a manufacturer's editor on another tab. */
  onOpenManufacturer?: (manufacturerId: string) => void;
  /** A quick-create in the ownership dialog minted an organization. */
  onOrgsChanged?: () => void;
}

export function ParentOrganizationEditor({
  isOpen,
  org,
  orgs,
  onClose,
  onSaved,
  onDeleted,
  onOpenBrand,
  onOpenManufacturer,
  onOrgsChanged,
}: Props) {
  // The org being edited. Starts as the prop and becomes the created doc after a
  // first save, so the sections below appear without closing the modal.
  const [current, setCurrent] = useState<ParentOrganization | null>(org);
  const [form, setForm] = useState<Form>(toForm(org));
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [parentQuery, setParentQuery] = useState('');
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const logoInput = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!isOpen) return;
    setCurrent(org);
    setForm(toForm(org));
    setConfirmDelete(false);
    setParentQuery('');
  }, [isOpen, org]);

  const isNew = !current;
  const slug = isNew ? slugifyParentOrgValue(form.name) : current.id;
  const set = <K extends keyof Form>(key: K, value: Form[K]) => setForm((f) => ({ ...f, [key]: value }));

  const forbidden = useMemo(
    () => (current ? forbiddenParentIds(current.id, orgs) : new Set<string>()),
    [current, orgs],
  );
  const parentOptions = useMemo(
    () =>
      orgs
        .filter((o) => !forbidden.has(o.id))
        .map((o) => ({ id: o.id, label: o.displayName || o.name || o.id, sub: o.country }))
        .filter((o) => optionMatches(o, parentQuery)),
    [orgs, forbidden, parentQuery],
  );
  const parentLabel = (id: string) => {
    const o = orgs.find((x) => x.id === id);
    return o ? o.displayName || o.name || o.id : id;
  };
  const childCount = current ? orgs.filter((o) => o.parentOrgId === current.id).length : 0;

  /**
   * The chain shown under the picker, computed from the FORM rather than the
   * saved document so it updates as a parent is picked — the point of showing it
   * is to see where a choice lands before committing to it. The org's own entry
   * in `orgs` is overridden for the same reason: the stored `parentOrgId` is one
   * save behind what the curator is looking at.
   */
  const ancestry = useMemo(() => {
    if (!current) return { nodes: [], truncated: false };
    const withPending = { ...current, parentOrgId: form.parentOrgId || undefined };
    return ancestryPath(
      withPending,
      orgs.map((o) => (o.id === current.id ? withPending : o)),
    );
  }, [current, form.parentOrgId, orgs]);

  /**
   * AI basics review. The suggestions land in a dialog rather than in the form:
   * a proposal is a claim to check, and the founding year in particular is the
   * field the model most often answers with the oldest BRAND's year instead of
   * the organization's. High-confidence fields start accepted, matching the
   * brand and manufacturer flows; everything else is opt-in.
   */
  const [aiRunning, setAiRunning] = useState(false);
  const [aiSuggestions, setAiSuggestions] = useState<Record<string, unknown> | null>(null);
  const [aiConfidence, setAiConfidence] = useState<Record<string, string>>({});
  const [aiAccepted, setAiAccepted] = useState<Record<string, boolean>>({});
  /**
   * The pre-run step. The button opens it rather than running straight away, so
   * there is somewhere to paste research BEFORE the model answers — the same
   * shape as the history and ownership flows. Blocks survive a Discard, so a
   * disappointing run can be retried with more context without re-pasting.
   */
  const [aiSetupOpen, setAiSetupOpen] = useState(false);
  const [aiContextBlocks, setAiContextBlocks] = useState<string[]>(['']);

  // Pasted research belongs to one organization; never carry it to the next.
  useEffect(() => {
    if (!isOpen) return;
    setAiContextBlocks(['']);
    setAiSetupOpen(false);
  }, [isOpen, org?.id]);

  const openAiEnrich = () => {
    if (!form.name.trim()) {
      toast.error('Give the organization a name first');
      return;
    }
    setAiSetupOpen(true);
  };

  const handleAiEnrich = async () => {
    if (!form.name.trim()) {
      toast.error('Give the organization a name first');
      return;
    }
    const contextBlocks = aiContextBlocks.map((b) => b.trim()).filter(Boolean);
    setAiRunning(true);
    try {
      const raw = await enrichParentOrgWithAI({
        ...(contextBlocks.length ? { additionalContext: contextBlocks } : {}),
        orgData: {
          name: form.name.trim(),
          ...(form.displayName.trim() ? { displayName: form.displayName.trim() } : {}),
          ...(form.description.trim() ? { description: form.description.trim() } : {}),
          ...(form.country.trim() ? { country: form.country.trim() } : {}),
          ...(form.founded.trim() && Number.isInteger(Number(form.founded))
            ? { founded: Number(form.founded) }
            : {}),
          ...(form.website.trim() ? { website: form.website.trim() } : {}),
          ...(form.alternativeNames.length ? { alternativeNames: form.alternativeNames } : {}),
          ...(form.abbreviations.length ? { abbreviations: form.abbreviations } : {}),
          status: form.status,
        },
      });
      // A rewritten description that drops any in-app link of the current one
      // is never offered — checked here as well as server-side.
      const { result, losses } = withholdLinkLosingDescriptions(raw, form.description, { html: ['description'] });
      const linkLoss = describeLinkLoss(losses);
      if (linkLoss) toast.error(linkLoss, { duration: 12000 });
      const keys = Object.keys(result.suggestions ?? {});
      if (keys.length === 0) {
        toast(
          contextBlocks.length
            ? 'No new suggestions — the context agrees with the record'
            : 'No new suggestions — the record looks complete',
        );
        return;
      }
      setAiSetupOpen(false);
      setAiSuggestions(result.suggestions);
      setAiConfidence(result.confidence ?? {});
      setAiAccepted(
        Object.fromEntries(keys.map((k) => [k, (result.confidence ?? {})[k] === 'high'])),
      );
    } catch (err) {
      console.error('Parent org AI enrichment failed:', err);
      toast.error(err instanceof Error ? err.message : 'AI enrichment failed');
    } finally {
      setAiRunning(false);
    }
  };

  /** Apply the ticked suggestions into the FORM — the curator still saves. */
  const applyAiSuggestions = () => {
    const suggestions = aiSuggestions ?? {};
    let applied = 0;
    for (const [key, value] of Object.entries(suggestions)) {
      if (!aiAccepted[key]) continue;
      applied += 1;
      if (key === 'founded') set('founded', String(value));
      else if (key === 'alternativeNames' || key === 'abbreviations') {
        set(key, Array.isArray(value) ? (value as string[]) : []);
      } else if (key === 'status') {
        set('status', value as Form['status']);
      } else if (key in form) {
        set(key as keyof Form, String(value) as never);
      }
    }
    setAiSuggestions(null);
    toast.success(`Applied ${applied} field${applied === 1 ? '' : 's'} — Save to keep them`);
  };

  const handleLogoFile = async (file: File) => {
    if (!slug) {
      toast.error('Enter a name first so the organization has an id');
      return;
    }
    setUploadingLogo(true);
    try {
      set('logo', await uploadParentOrganizationLogo(slug, file, file.name));
      toast.success('Logo uploaded — Save to keep it');
    } catch (err) {
      console.error('Logo upload failed', err);
      toast.error(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setUploadingLogo(false);
      if (logoInput.current) logoInput.current.value = '';
    }
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      toast.error('Name is required');
      return;
    }
    if (form.founded.trim() && !Number.isInteger(Number(form.founded))) {
      toast.error('Founded must be a year');
      return;
    }
    const patch = formToPatch(form);
    setSaving(true);
    try {
      if (isNew) {
        if (!slug) {
          toast.error('The name needs at least one letter or number');
          return;
        }
        // A create writes no cleared fields at all — drop the nulls.
        const fields = Object.fromEntries(
          Object.entries(patch).filter(([, v]) => v !== null),
        ) as Omit<ParentOrganization, 'id' | 'createdAt' | 'updatedAt'>;
        const created = await createParentOrganization(slug, fields);
        if (!created) {
          toast.error(`An organization with the id "${slug}" already exists`);
          return;
        }
        const saved = { id: slug, ...fields } as ParentOrganization;
        setCurrent(saved);
        toast.success('Organization created');
        onSaved(saved);
      } else {
        await updateParentOrganization(current.id, patch);
        const saved = {
          ...current,
          ...Object.fromEntries(Object.entries(patch).map(([k, v]) => [k, v ?? undefined])),
        } as ParentOrganization;
        setCurrent(saved);
        toast.success('Organization saved');
        onSaved(saved);
      }
    } catch (err) {
      console.error('Failed to save parent organization:', err);
      toast.error(err instanceof Error ? err.message : 'Could not save');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!current) return;
    setSaving(true);
    try {
      await deleteParentOrganization(current.id);
      toast.success('Organization deleted');
      onDeleted(current.id);
    } catch (err) {
      console.error('Failed to delete parent organization:', err);
      toast.error('Could not delete');
    } finally {
      setSaving(false);
    }
  };

  const inputClass =
    'w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-500 focus:border-brand-500';

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => (saving ? undefined : onClose())}
      title={isNew ? 'New parent organization' : current.displayName || current.name}
      size="full"
    >
      <div className="space-y-6">
        <section className="space-y-4">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">Name</label>
              <input
                type="text"
                value={form.name}
                aria-label="Name"
                onChange={(e) => set('name', e.target.value)}
                placeholder="The Swatch Group Ltd"
                className={inputClass}
              />
              <p className="mt-1 text-xs text-gray-500">
                Id: <span className="font-mono">{slug || '—'}</span>
                {!isNew && ' (fixed once created)'}
              </p>
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">Display name</label>
              <input
                type="text"
                value={form.displayName}
                aria-label="Display name"
                onChange={(e) => set('displayName', e.target.value)}
                placeholder="Swatch Group"
                className={inputClass}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            <CountrySelect value={form.country} onChange={(v) => set('country', v)} label="Country" />
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">Founded</label>
              <input
                type="number"
                value={form.founded}
                aria-label="Founded"
                onChange={(e) => set('founded', e.target.value)}
                placeholder="1983"
                className={inputClass}
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">Status</label>
              <select
                value={form.status}
                aria-label="Status"
                onChange={(e) => set('status', e.target.value as Form['status'])}
                className={`${inputClass} bg-white`}
              >
                {STATUSES.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">Website</label>
              <input
                type="url"
                value={form.website}
                aria-label="Website"
                onChange={(e) => set('website', e.target.value)}
                placeholder="https://"
                className={inputClass}
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">
                Owned by <span className="font-normal text-gray-400">(parent organization)</span>
              </label>
              <EntityPicker
                value={form.parentOrgId}
                selectedLabel={form.parentOrgId ? parentLabel(form.parentOrgId) : undefined}
                options={parentOptions}
                query={parentQuery}
                onQueryChange={setParentQuery}
                onSelect={(id) => set('parentOrgId', id)}
                ariaLabel="Owning parent organization"
                placeholder="Search parent organizations…"
                allowClear
              />
              <p className="mt-1 text-xs text-gray-500">
                The organization itself and everything it owns are left out, so the tree can't loop.
              </p>
              {ancestry.nodes.length > 0 && (
                <p className="mt-1 text-xs text-gray-600">
                  {ancestry.nodes.map((node, i) => (
                    <span key={node.id}>
                      {i > 0 && <span className="mx-1 text-gray-300">·</span>}
                      <span className={node.id === current?.id ? 'font-medium text-gray-800' : ''}>
                        {node.label}
                      </span>
                    </span>
                  ))}
                  {ancestry.truncated && (
                    <span className="ml-1 text-amber-700" title="The parentOrgId chain loops">
                      — chain loops, shown truncated
                    </span>
                  )}
                </p>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <TagInput
              label="Alternative names"
              values={form.alternativeNames}
              onChange={(v) => set('alternativeNames', v)}
              placeholder="Legacy spellings — used to match brand parentOrg strings"
            />
            <TagInput
              label="Abbreviations"
              values={form.abbreviations}
              onChange={(v) => set('abbreviations', v)}
              placeholder="e.g. LVMH"
            />
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Logo</label>
            <div className="flex items-start gap-3">
              {form.logo ? (
                <img
                  src={form.logo}
                  alt="Logo"
                  className="h-16 w-16 shrink-0 rounded border border-gray-200 bg-gray-50 object-contain"
                />
              ) : (
                <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded border border-dashed border-gray-300 bg-gray-50 text-[10px] text-gray-400">
                  No logo
                </div>
              )}
              <div className="flex-1 space-y-2">
                <div className="flex items-center gap-2">
                  <input
                    ref={logoInput}
                    type="file"
                    accept="image/*"
                    aria-label="Upload logo"
                    disabled={uploadingLogo || !slug}
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) void handleLogoFile(file);
                    }}
                    className="text-xs text-gray-600"
                  />
                  {uploadingLogo && <span className="text-xs text-gray-400">Uploading…</span>}
                  {form.logo && !uploadingLogo && (
                    <button
                      type="button"
                      onClick={() => set('logo', '')}
                      className="text-xs text-red-600 underline hover:text-red-800"
                    >
                      Remove
                    </button>
                  )}
                </div>
                <input
                  type="text"
                  value={form.logo}
                  aria-label="Logo URL"
                  onChange={(e) => set('logo', e.target.value)}
                  placeholder="https:// or upload above"
                  className={inputClass}
                />
                <input
                  type="text"
                  value={form.logoSourceUrl}
                  aria-label="Logo source URL"
                  onChange={(e) => set('logoSourceUrl', e.target.value)}
                  placeholder="Where the logo came from (optional attribution)"
                  className={inputClass}
                />
              </div>
            </div>
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Description</label>
            {/* entityLinks: without the mark registered, TipTap drops every
                watchkeeper:// anchor on parse, and the next edit saves the
                description without its links. */}
            <RichTextEditor
              value={form.description}
              onChange={(html) => set('description', html)}
              minHeight={180}
              entityLinks
            />
          </div>

          <div className="flex items-center justify-between border-t border-gray-100 pt-3">
            {!isNew ? (
              <Button variant="danger" onClick={() => setConfirmDelete(true)} disabled={saving}>
                Delete
              </Button>
            ) : (
              <span />
            )}
            <div className="flex gap-2">
              <Button
                variant="secondary"
                onClick={openAiEnrich}
                loading={aiRunning}
                disabled={saving || aiRunning}
                title="Propose corporate basics with AI, then accept them field by field"
              >
                ✨ AI Enrich Basics
              </Button>
              <Button variant="secondary" onClick={onClose} disabled={saving}>
                Close
              </Button>
              <Button onClick={() => void handleSave()} loading={saving}>
                {isNew ? 'Create organization' : 'Save details'}
              </Button>
            </div>
          </div>
        </section>

        {current ? (
          <>
            <PortfolioSection
              org={current}
              onOpenBrand={onOpenBrand}
              onOpenManufacturer={onOpenManufacturer}
            />
            <OwnershipEventsSection
              org={current}
              orgs={orgs}
              onOrgChange={(next) => { setCurrent(next); onSaved(next); }}
              onOrgsChanged={onOrgsChanged}
            />
            <ParentOrgTimelineSection org={current} onOrgChange={(next) => { setCurrent(next); onSaved(next); }} />
          </>
        ) : (
          <p className="text-xs text-gray-500">
            Ownership events and the history timeline appear once the organization is created.
          </p>
        )}
      </div>

      <Modal
        isOpen={aiSetupOpen}
        onClose={() => (aiRunning ? undefined : setAiSetupOpen(false))}
        title="AI Enrich Basics"
        size="lg"
      >
        <div className="space-y-3">
          {countEntityLinks(form.description) > 0 && (
            <DescriptionLinksWarning count={countEntityLinks(form.description)} />
          )}
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">
              Supply context <span className="font-normal normal-case">(optional)</span>
            </span>
            <button
              type="button"
              className="text-[11px] text-indigo-600 hover:underline disabled:opacity-50"
              disabled={aiRunning}
              onClick={() => setAiContextBlocks((b) => [...b, ''])}
            >
              + Add another source
            </button>
          </div>
          <p className="text-[11px] text-gray-500">
            Paste research — Gemini, Wikipedia, a company filing — one block per source. The model
            treats it as ground truth, and may rewrite a field it contradicts, description included.
            Leave it empty to enrich from the model&rsquo;s own knowledge.
          </p>
          {aiContextBlocks.map((block, i) => (
            <div key={i} className="flex gap-2">
              <textarea
                value={block}
                disabled={aiRunning}
                aria-label={`Context source ${i + 1}`}
                onChange={(e) =>
                  setAiContextBlocks((prev) => prev.map((b, j) => (j === i ? e.target.value : b)))
                }
                rows={i === 0 ? 8 : 4}
                placeholder={`Source ${i + 1}`}
                className="flex-1 rounded border border-gray-300 px-2 py-1 font-mono text-sm"
              />
              {aiContextBlocks.length > 1 && (
                <button
                  type="button"
                  className="self-start text-[11px] text-red-600 hover:underline"
                  disabled={aiRunning}
                  onClick={() => setAiContextBlocks((prev) => prev.filter((_, j) => j !== i))}
                >
                  Remove
                </button>
              )}
            </div>
          ))}
          <div className="flex justify-end gap-2 border-t border-gray-100 pt-3">
            <Button variant="secondary" onClick={() => setAiSetupOpen(false)} disabled={aiRunning}>
              Cancel
            </Button>
            <Button onClick={() => void handleAiEnrich()} loading={aiRunning} disabled={aiRunning}>
              {countEntityLinks(form.description) > 0
                ? 'Proceed'
                : aiContextBlocks.some((b) => b.trim())
                  ? 'Propose with context'
                  : 'Propose basics'}
            </Button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={aiSuggestions !== null}
        onClose={() => setAiSuggestions(null)}
        title="AI suggestions — review before applying"
        size="lg"
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            Ticked fields are written into the form; you still press Save. High-confidence
            suggestions start ticked.{' '}
            <span className="text-amber-700">
              Check the founding year: a model routinely answers with the oldest brand&rsquo;s year
              rather than the organization&rsquo;s.
            </span>
          </p>
          <div className="overflow-x-auto rounded-lg border border-gray-200">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 text-left text-[11px] uppercase text-gray-500">
                <tr>
                  <th className="w-10 px-3 py-2" />
                  <th className="px-3 py-2">Field</th>
                  <th className="px-3 py-2">Current</th>
                  <th className="px-3 py-2">Suggested</th>
                  <th className="px-3 py-2">Confidence</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {Object.entries(aiSuggestions ?? {}).map(([key, value]) => {
                  const current = form[key as keyof Form];
                  const show = (v: unknown) =>
                    Array.isArray(v) ? v.join(', ') : reviewText(String(v ?? '')) || '—';
                  const confidence = aiConfidence[key] ?? 'medium';
                  return (
                    <tr key={key}>
                      <td className="px-3 py-2 align-top">
                        <input
                          type="checkbox"
                          aria-label={`Accept ${key}`}
                          checked={!!aiAccepted[key]}
                          onChange={(e) =>
                            setAiAccepted((prev) => ({ ...prev, [key]: e.target.checked }))
                          }
                        />
                      </td>
                      <td className="px-3 py-2 align-top font-mono text-xs text-gray-700">{key}</td>
                      <td className="max-w-xs break-words px-3 py-2 align-top text-xs text-gray-500">
                        {show(current)}
                      </td>
                      <td className="max-w-md break-words px-3 py-2 align-top text-gray-800">
                        {show(value)}
                      </td>
                      <td className="px-3 py-2 align-top">
                        <span
                          className={`rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase ${
                            confidence === 'high'
                              ? 'bg-emerald-100 text-emerald-800'
                              : confidence === 'low'
                                ? 'bg-red-100 text-red-800'
                                : 'bg-amber-100 text-amber-800'
                          }`}
                        >
                          {confidence}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setAiSuggestions(null)}>
              Discard
            </Button>
            <Button
              onClick={applyAiSuggestions}
              disabled={Object.values(aiAccepted).every((v) => !v)}
            >
              Apply {Object.values(aiAccepted).filter(Boolean).length} field
              {Object.values(aiAccepted).filter(Boolean).length === 1 ? '' : 's'}
            </Button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={confirmDelete}
        onClose={() => (saving ? undefined : setConfirmDelete(false))}
        title="Delete parent organization"
        size="sm"
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-700">
            Delete <span className="font-medium">{current?.displayName || current?.name}</span>?
          </p>
          {childCount > 0 && (
            <p className="rounded bg-amber-50 px-3 py-2 text-xs text-amber-800">
              {childCount} other organization{childCount === 1 ? '' : 's'} list this one as their
              owner and will point at nothing.
            </p>
          )}
          <p className="text-xs text-gray-500">
            Brand and manufacturer <code>parentOrg</code> strings are not affected — they don't point
            at these documents yet.
          </p>
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setConfirmDelete(false)} disabled={saving}>
              Cancel
            </Button>
            <Button variant="danger" onClick={() => void handleDelete()} loading={saving}>
              Delete
            </Button>
          </div>
        </div>
      </Modal>
    </Modal>
  );
}
